import { useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Eye, Search } from 'lucide-react';
import { Pagination } from '../../../components/ui/Pagination';
import type { WeldAnalyticsBundle, WeldSummary } from '../types/weld';
import { formatCount, formatNumber } from '../../ndt/utils/format';
import { glossaryText } from '../utils/weldFieldGlossary';
import { buildWeldPriorityMap } from '../utils/weldReviewPriority';
import { AnalyticsSegmentedControl } from './shared/AnalyticsSegmentedControl';
import { AnalyticsDataTable, type AnalyticsColumn } from './shared/AnalyticsDataTable';
import { ColumnHeaderHelp } from './shared/ColumnHeaderHelp';
import { AnomalyMetricsPanel } from './AnomalyMetricsPanel';
import type { PendingWeldDrillDown } from './dashboard/drilldown/drillDownTypes';

interface StatisticsPanelProps {
  bundle: WeldAnalyticsBundle;
  appliedJobNumbers?: string[];
  onOpenWeldDrillDown?: (target: PendingWeldDrillDown) => void;
  /** Combined only. Extra statistics sub-tabs after the weld ones. */
  extraTabs?: { label: string; content: ReactNode }[];
}

const SUBTABS = ['Weld summary', 'Compliance', 'Anomaly metrics'] as const;

/** Flip to false to restore the fat Statistics segmented sub-tab strip. */
const USE_STATISTICS_VIEW_DROPDOWN = false;

type SortField = 'weld_rank' | 'failed_passes' | 'anomalous_passes' | 'total_passes';

function sortWelds(rows: WeldSummary[], field: SortField, dir: 'asc' | 'desc'): WeldSummary[] {
  const mult = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[field] ?? (field === 'weld_rank' ? 99999 : 0);
    const bv = b[field] ?? (field === 'weld_rank' ? 99999 : 0);
    return (Number(av) - Number(bv)) * mult;
  });
}

const PRIORITY_CLASS: Record<string, string> = {
  Critical: 'font-semibold text-readable-red',
  High: 'font-medium text-readable-red',
  Medium: 'font-medium text-[#B38600]',
  Watch: 'text-blueprint-blue',
};

export const StatisticsPanel = ({
  bundle,
  appliedJobNumbers = [],
  onOpenWeldDrillDown,
  extraTabs,
}: StatisticsPanelProps) => {
  const [subtab, setSubtab] = useState<string>('Weld summary');
  const [search, setSearch] = useState('');
  const [systemFilter, setSystemFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [sortField, setSortField] = useState<SortField>('weld_rank');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const allWelds = useMemo(() => bundle.welds ?? [], [bundle.welds]);
  const priorityByWeld = useMemo(() => buildWeldPriorityMap(bundle), [bundle]);

  const systemOptions = useMemo(
    () =>
      Array.from(new Set(allWelds.map((w) => w.welding_system || 'Unknown'))).sort(),
    [allWelds],
  );

  const jobOptions = useMemo(
    () => Array.from(new Set(allWelds.map((w) => w.job_number).filter(Boolean))).sort(),
    [allWelds],
  );

  const showJobColumn = appliedJobNumbers.length > 1 || jobOptions.length > 1;

  const filteredWelds = useMemo(() => {
    let rows = allWelds;
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((w) => String(w.weld_number).toLowerCase().includes(q));
    }
    if (systemFilter) {
      rows = rows.filter((w) => (w.welding_system || 'Unknown') === systemFilter);
    }
    if (jobFilter) {
      rows = rows.filter((w) => w.job_number === jobFilter);
    }
    return sortWelds(rows, sortField, sortDir);
  }, [allWelds, search, systemFilter, jobFilter, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredWelds.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageWelds = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredWelds.slice(start, start + pageSize);
  }, [filteredWelds, safePage, pageSize]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'weld_rank' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-40" />;
    return sortDir === 'asc' ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    );
  };

  const sortableHeader = (label: string, field: SortField, helpKey: string) => (
    <button
      type="button"
      onClick={() => toggleSort(field)}
      className="inline-flex items-center text-left hover:text-readable-red"
    >
      <ColumnHeaderHelp label={label} help={glossaryText(helpKey)} />
      <SortIcon field={field} />
    </button>
  );

  const weldColumns = useMemo((): AnalyticsColumn<WeldSummary>[] => {
    const cols: AnalyticsColumn<WeldSummary>[] = [
      {
        id: 'sl_no',
        header: 'Sl. No.',
        cell: (_w, index) => (safePage - 1) * pageSize + index + 1,
        className: 'font-medium text-blueprint-blue',
      },
      {
        id: 'weld_number',
        header: 'Weld #',
        cell: (w) => w.weld_number,
        className: 'font-medium',
      },
    ];
    if (showJobColumn) {
      cols.push({
        id: 'job_number',
        header: 'Job #',
        cell: (w) => w.job_number ?? '—',
      });
    }
    cols.push(
      {
        id: 'welding_system',
        header: 'System',
        cell: (w) => w.welding_system ?? '—',
      },
      {
        id: 'review_priority',
        header: <ColumnHeaderHelp label="Priority" help={glossaryText('review_priority')} />,
        cell: (w) => {
          const p =
            priorityByWeld.get(`${w.job_number ?? ''}::${w.weld_number}`) ?? '—';
          return <span className={PRIORITY_CLASS[p] ?? ''}>{p}</span>;
        },
      },
      {
        id: 'total_passes',
        header: sortableHeader('Total passes', 'total_passes', 'total_passes'),
        cell: (w) => formatCount(w.total_passes ?? 0),
      },
      {
        id: 'failed_passes',
        header: sortableHeader('Failed passes', 'failed_passes', 'failed_passes'),
        cell: (w) => (
          <span className={(w.failed_passes ?? 0) > 0 ? 'font-medium text-readable-red' : ''}>
            {formatCount(w.failed_passes ?? 0)}
          </span>
        ),
      },
      {
        id: 'anomalous_passes',
        header: sortableHeader('Anomalous passes', 'anomalous_passes', 'anomalous_passes'),
        cell: (w) => (
          <span className={(w.anomalous_passes ?? 0) > 0 ? 'font-medium text-[#B38600]' : ''}>
            {formatCount(w.anomalous_passes ?? 0)}
          </span>
        ),
      },
      {
        id: 'anomaly_pct',
        header: <ColumnHeaderHelp label="Anomaly %" help={glossaryText('anomaly_pct')} />,
        cell: (w) =>
          w.anomaly_pct != null ? `${formatNumber(w.anomaly_pct, 1)}%` : '—',
      },
      {
        id: 'actions',
        header: <span aria-hidden="true" />,
        cell: (w) => (
          <button
            type="button"
            title="Open weld drill-down"
            aria-label={`Open drill-down for weld ${w.weld_number}`}
            disabled={!onOpenWeldDrillDown}
            onClick={(e) => {
              e.stopPropagation();
              onOpenWeldDrillDown?.({
                weldNumber: String(w.weld_number),
                jobNumber: String(w.job_number ?? ''),
                scope: 'single',
              });
            }}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-blueprint-blue hover:bg-hero-red-10 hover:text-readable-red disabled:opacity-40"
          >
            <Eye className="h-4 w-4" />
          </button>
        ),
        className: 'w-10',
      },
    );
    return cols;
  }, [showJobColumn, sortField, sortDir, priorityByWeld, onOpenWeldDrillDown, safePage, pageSize]);

  const complianceRows = bundle.compliance?.per_parameter ?? [];
  const complianceColumns: AnalyticsColumn<(typeof complianceRows)[number]>[] = [
    {
      id: 'parameter',
      header: 'Parameter',
      cell: (r) => <span className="font-medium">{r.parameter}</span>,
    },
    {
      id: 'checked',
      header: 'Checked',
      cell: (r) => formatCount(r.checked),
    },
    {
      id: 'compliant',
      header: 'Compliant',
      cell: (r) => formatCount(r.compliant),
    },
    {
      id: 'non_compliant',
      header: <ColumnHeaderHelp label="Non-compliant" help={glossaryText('non_compliant')} />,
      cell: (r) => (
        <span className={r.non_compliant > 0 ? 'font-medium text-readable-red' : ''}>
          {formatCount(r.non_compliant)}
        </span>
      ),
    },
    {
      id: 'compliance_rate_pct',
      header: <ColumnHeaderHelp label="Compliance rate %" help={glossaryText('compliance_rate_pct')} />,
      cell: (r) => `${formatNumber(r.compliance_rate_pct, 1)}%`,
    },
  ];

  const viewOptions = [...SUBTABS, ...(extraTabs?.map((tab) => tab.label) ?? [])];

  return (
    <div className="space-y-4">
      {USE_STATISTICS_VIEW_DROPDOWN ? (
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="statistics-view" className="text-xs font-medium text-heritage-grey">
            View:
          </label>
          <select
            id="statistics-view"
            data-testid="statistics-view-dropdown"
            value={subtab}
            onChange={(e) => setSubtab(e.target.value)}
            className="rounded-lg border border-[#E2E5EA] bg-white px-3 py-1.5 text-sm font-medium text-blueprint-blue focus:border-blueprint-blue focus:outline-none"
          >
            {viewOptions.map((label) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <AnalyticsSegmentedControl
          ariaLabel="Statistics view"
          tabs={viewOptions.map((label) => ({
            id: label,
            label,
          }))}
          activeId={subtab}
          onChange={setSubtab}
        />
      )}

      {extraTabs?.find((tab) => tab.label === subtab)?.content ?? null}

      {subtab === 'Weld summary' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[#E2E5EA] bg-page-bg/50 p-3">
            <div className="min-w-[160px] flex-1">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-heritage-grey" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Weld number…"
                  className="w-full rounded-lg border border-[#E2E5EA] bg-white py-2 pl-8 pr-3 text-sm text-text-grey focus:border-blueprint-blue focus:outline-none"
                />
              </div>
            </div>
            <div className="min-w-[140px]">
              <select
                value={systemFilter}
                onChange={(e) => {
                  setSystemFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-lg border border-[#E2E5EA] bg-white px-3 py-2 text-sm text-text-grey focus:border-blueprint-blue focus:outline-none"
              >
                <option value="">All systems</option>
                {systemOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            {showJobColumn && (
              <div className="min-w-[120px]">
                <select
                  value={jobFilter}
                  onChange={(e) => {
                    setJobFilter(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-[#E2E5EA] bg-white px-3 py-2 text-sm text-text-grey focus:border-blueprint-blue focus:outline-none"
                >
                  <option value="">All jobs</option>
                  {jobOptions.map((j) => (
                    <option key={j} value={j}>
                      {j}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {filteredWelds.length > 0 && (
            <p className="text-xs text-heritage-grey">
              Showing {formatCount((safePage - 1) * pageSize + 1)}–
              {formatCount(Math.min(safePage * pageSize, filteredWelds.length))} of{' '}
              {formatCount(filteredWelds.length)} welds
            </p>
          )}

          <AnalyticsDataTable
            columns={weldColumns}
            data={pageWelds}
            rowKey={(w) => `${w.job_number}-${w.weld_number}`}
            emptyMessage={
              allWelds.length === 0
                ? 'No analytics data for these jobs yet.'
                : 'No welds match the current filters.'
            }
            truncationBanner={undefined}
          />

          <Pagination
            currentPage={safePage}
            totalItems={filteredWelds.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
            pageSizeOptions={[10, 25, 50]}
          />
        </div>
      )}

      {subtab === 'Compliance' && (
        <div className="space-y-3">
          {bundle.compliance && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-[#E2E5EA] bg-page-bg/40 p-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-heritage-grey">
                  <ColumnHeaderHelp
                    label="Overall compliance"
                    help={glossaryText('overall_compliance_rate_pct')}
                  />
                </p>
                <p className="mt-1 text-2xl font-semibold text-blueprint-blue">
                  {bundle.compliance.overall_compliance_rate_pct != null
                    ? `${formatNumber(bundle.compliance.overall_compliance_rate_pct, 1)}%`
                    : '—'}
                </p>
              </div>
              <div className="rounded-xl border border-[#E2E5EA] bg-page-bg/40 p-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-heritage-grey">
                  <ColumnHeaderHelp
                    label="Total passes checked"
                    help={glossaryText('total_passes_compliance')}
                  />
                </p>
                <p className="mt-1 text-2xl font-semibold text-blueprint-blue">
                  {formatCount(bundle.compliance.total_passes ?? bundle.totalPasses ?? 0)}
                </p>
              </div>
            </div>
          )}
          <AnalyticsDataTable
            columns={complianceColumns}
            data={complianceRows}
            rowKey={(r) => r.parameter}
            emptyMessage="No compliance data for the selected jobs."
          />
        </div>
      )}

      {subtab === 'Anomaly metrics' && (
        <AnomalyMetricsPanel metrics={bundle.anomalyMetrics} />
      )}
    </div>
  );
};
